import { Box } from '@material-ui/core'
import { CustomTooltip } from 'components/facets'
import { RarityBadge } from 'components/facets/RarityBadge'
import { ARTICLE_STATES, OrderArticle } from 'shared'
import { getProductImageUrl } from 'utils/imageUtils'
import { CardInfoBox } from './CardInfoBox'
import { PickingButton } from './PickingButton'

export interface OrderVisualArticleProps {
  article: OrderArticle
  fullScreen?: boolean
  horizontalView?: boolean
  hideBar?: boolean
  type?: string
  handlePick?: (article: OrderArticle, quantity: number) => void
  handleCompletePicking?: (article: OrderArticle, readonly: boolean) => void
  handleReportIssue?: (article: OrderArticle) => void
  handleBackToPicking?: (article: OrderArticle) => void
  handleIssues?: (a: OrderArticle) => void
  missingLocations?: boolean
  highlightCard: boolean
}

export const OrderVisualArticle = ({
  article,
  type,
  horizontalView,
  handleCompletePicking,
  handleReportIssue,
  handleBackToPicking,
  handleIssues,
  missingLocations,
  highlightCard,
}: OrderVisualArticleProps): JSX.Element => {
  const imgSrc = getProductImageUrl(article.idProduct)

  const headerCard = (article: OrderArticle) => {
    if (horizontalView) {
      return (
        <div
          className={`heading-container order-visual-article d-flex flex-column align-items-start`}
        >
          <div className="d-inline-block text-truncate w-100 fw-bold order-visual-article-name">
            <span>{article.getLocalizedName()}</span>
            {article.idLanguage !== 1 && <span className="ms-1">[{article.card.name}]</span>}
          </div>
          <div className="d-flex flex-row text-truncate align-items-start w-100 order-visual-article-expansion">
            <Box width={32} className="me-1">
              <RarityBadge
                rarity={article.card.rarityStr ?? ''}
                idGame={article.card.idGame!}
                className="ms-1"
              />
            </Box>
            <span className="me-1">
              {' '}
              # {article.card.cn} - {article.card.set} -
            </span>
            <span className="text-truncate">{article.card.expansion}</span>
          </div>
        </div>
      )
    } else {
      return (
        <div
          className={`heading-container order-visual-article d-flex flex-column align-items-start`}
        >
          <div className="d-inline-block text-truncate w-100 fw-bold order-visual-article-name">
            <span>{article.getLocalizedName()}</span>
            {article.idLanguage !== 1 && <span className="ms-1">[{article.card.name}]</span>}
          </div>
          <div className="d-inline-block text-truncate align-items-start w-100 order-visual-article-expansion">
            <span className="me-1">
              {' '}
              # {article.card.cn} - {article.card.set} -
            </span>
            <span className="text-truncate">{article.card.expansion}</span>
          </div>
        </div>
      )
    }
  }

  const pickingButtons = (type: string) => {
    return (
      <>
        {type === 'toPick' && (
          <PickingButton
            type="pick"
            fullWidth={true}
            article={article}
            handleCompletePicking={() => handleCompletePicking!(article, false)}
          />
        )}
        {type === 'toPick' && (
          <div className="mt-1">
            <PickingButton
              type="reportIssue"
              fullWidth={true}
              article={article}
              handleReportIssue={handleReportIssue}
            />
          </div>
        )}

        {type === 'picked' &&
          (article.articleState === ARTICLE_STATES.PICKED ? (
            <PickingButton
              type="picked"
              fullWidth={true}
              article={article}
              handleCompletePicking={() => handleCompletePicking!(article, true)}
            />
          ) : (
            <PickingButton
              type="issues"
              fullWidth={true}
              article={article}
              handleIssues={handleIssues}
            />
          ))}

        {['picked', 'withIssues'].includes(type!) && (
          <div className="mt-1">
            <PickingButton
              type="backToPicking"
              fullWidth={true}
              article={article}
              handleBackToPicking={handleBackToPicking}
            />
          </div>
        )}
      </>
    )
  }

  const errorClass = missingLocations ? 'isError' : ''

  if (horizontalView) {
    return (
      <div
        className={`order-visual-article-container-horizontal ${type} ${errorClass} ${
          highlightCard ? 'highlight-card' : ''
        }`}
      >
        {headerCard(article)}
        <div className="d-flex w-100 flex-row mt-1">
          <div
            className="order-visual-article-img-container-horizontal me-2"
            style={{ width: '36%' }}
          >
            <img src={imgSrc} />
          </div>
          <div className="d-flex flex-column" style={{ width: '64%' }}>
            <CardInfoBox article={article} type={type} horizontalView={horizontalView} />
            {pickingButtons(type!)}
          </div>
        </div>
      </div>
    )
  } else
    return (
      <div
        className={`order-visual-article-container ${type} ${errorClass} ${
          highlightCard ? 'highlight-card' : ''
        }`}
      >
        <div className="d-flex w-100 flex-column order-visual-article-body">
          <CustomTooltip title={headerCard(article)}>{headerCard(article)}</CustomTooltip>
          <div className="order-visual-article-img-container mb-1">
            <img src={imgSrc} />
          </div>
          <CardInfoBox article={article} type={type} horizontalView={horizontalView} />
        </div>
        {pickingButtons(type!)}
      </div>
    )
}
